import { useCallback, useEffect, useState } from 'react';
import { Trophy, Settings2, Flame, Crown, Sword, RefreshCw, Share2, Zap } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import Avatar from '../../components/ui/avatar-initials';
import { fmtDelta, deltaClass, firstName, shareText } from '../../lib/play';
import SeasonStrip from '../../components/play/SeasonStrip';
import MyPlayCard from '../../components/play/MyPlayCard';
import RecentMatches from '../../components/play/RecentMatches';
import LeaguesCard from '../../components/play/LeaguesCard';
import PlaySettingsSheet from '../../components/play/PlaySettingsSheet';
import SeasonSheet from '../../components/play/SeasonSheet';

export default function PlayTab({ ctx }) {
  const { group, user, isAdmin, fetchGroup } = ctx;
  const groupId = group?.id;
  const [data, setData] = useState(null);
  const [seasonId, setSeasonId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showSeason, setShowSeason] = useState(false);

  const load = useCallback(async () => {
    if (!groupId) return;
    try {
      const r = await api.get(`/groups/${groupId}/play/leaderboard`, { params: seasonId ? { season_id: seasonId } : {} });
      setData(r.data);
    } finally {
      setLoading(false);
    }
  }, [groupId, seasonId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener('play:changed', h);
    return () => window.removeEventListener('play:changed', h);
  }, [load]);

  if (loading && !data) {
    return <div className="space-y-3" data-testid="play-tab-loading">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 rounded-2xl" />)}</div>;
  }
  if (!data) return null;

  const players = data.players || [];
  const label = data.season ? data.season.name : 'All-time';

  const share = () => {
    const medals = ['🥇', '🥈', '🥉'];
    const lines = players.slice(0, 5).map((p, i) => `${medals[i] || `${i + 1}.`} ${p.name} — ${p.rating} (${p.wins}W-${p.losses}L)`);
    shareText(`🏆 ${group.name} · ${label} standings\n${lines.join('\n')}\n\nvia SplitEasy`);
  };

  return (
    <div className="space-y-3" data-testid="play-tab-content">
      <SeasonStrip
        data={data}
        isAdmin={isAdmin}
        onManage={() => setShowSeason(true)}
        onPick={(id) => setSeasonId(id)}
      />

      <div className="rounded-2xl border-2 border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-background to-background overflow-hidden" data-testid="play-leaderboard">
        <div className="flex items-center justify-between gap-2 px-4 pt-4 pb-2">
          <div className="flex items-center gap-2 min-w-0">
            {group?.logo_url ? (
              <img src={`${process.env.REACT_APP_BACKEND_URL}${group.logo_url}`} alt="" className="w-7 h-7 rounded-full object-cover border border-amber-500/40 shrink-0" data-testid="play-group-logo" />
            ) : (
              <Trophy className="w-4 h-4 text-amber-600 dark:text-amber-300" />
            )}
            <h3 className="font-display text-sm font-bold uppercase tracking-[0.16em] truncate">Leaderboard · {label}</h3>
          </div>
          <div className="flex items-center gap-1">
            <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full" onClick={load} data-testid="play-refresh-btn" title="Refresh">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
            {players.length > 0 && (
              <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full" onClick={share} data-testid="play-share-btn" title="Share standings">
                <Share2 className="w-3.5 h-3.5" />
              </Button>
            )}
            {isAdmin && (
              <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full" onClick={() => setShowSettings(true)} data-testid="play-settings-btn" title="Point system settings">
                <Settings2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        </div>

        {players.length === 0 ? (
          <div className="px-4 pb-5 pt-2 text-center" data-testid="play-empty">
            <Zap className="w-8 h-8 mx-auto text-amber-500/60 mb-2" />
            <p className="font-semibold text-sm">No matches yet</p>
            <p className="text-xs text-muted-foreground mt-1">
              Open a session → <span className="font-semibold">⚡ Court Mode</span> to shuffle teams and log your first game.
              {data.can_score ? '' : ' Ask an admin to make you a scorer.'}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border/60" data-testid="play-leaderboard-list">
            {players.map((p) => (
              <LeaderRow key={p.user_id} p={p} isKing={p.user_id === data.king_id} isMe={p.user_id === user?.id} />
            ))}
          </ul>
        )}
      </div>

      <MyPlayCard me={data.me} total={players.length} />
      <LeaguesCard groupId={group?.id} members={data.members || []} userId={user?.id} />
      <RecentMatches matches={data.recent_matches} userId={user?.id} canScore={data.can_score} isAdmin={isAdmin} onChanged={load} />

      <PlaySettingsSheet open={showSettings} onClose={() => setShowSettings(false)} group={group} onSaved={() => { fetchGroup?.(); load(); }} />
      <SeasonSheet open={showSeason} onClose={() => setShowSeason(false)} group={group} data={data} onChanged={() => { setSeasonId(null); load(); }} />
    </div>
  );
}

function LeaderRow({ p, isKing, isMe }) {
  return (
    <li className={`flex items-center gap-3 px-4 py-2.5 ${isMe ? 'bg-amber-500/10' : ''}`} data-testid={`leader-row-${p.user_id}`}>
      <span className={`w-6 text-center font-display font-black tabular-nums ${p.rank <= 3 ? 'text-amber-600 dark:text-amber-300 text-lg' : 'text-sm text-muted-foreground'}`}>
        {p.rank}
      </span>
      <div className="relative">
        <Avatar name={p.name} size="sm" />
        {isKing && <Crown className="w-4 h-4 text-amber-500 absolute -top-2 -right-1 rotate-12 drop-shadow" data-testid="king-crown" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm truncate flex items-center gap-1.5">
          {firstName(p.name)}
          {p.on_fire && <Flame className="w-3.5 h-3.5 text-orange-500" title={`${p.streak} wins in a row`} />}
          {p.giant_slayer_wins > 0 && <Sword className="w-3.5 h-3.5 text-violet-500" title={`${p.giant_slayer_wins} giant slayer win(s)`} />}
          {p.provisional && <span className="text-xs px-1.5 rounded-full bg-muted text-muted-foreground font-bold">PLACING</span>}
        </p>
        <p className="text-xs text-muted-foreground tabular-nums">
          {p.wins}W-{p.losses}L
          <span className="ml-2 inline-flex gap-0.5 align-middle">
            {(p.form || []).map((f, i) => <span key={i} className={`w-1.5 h-1.5 rounded-full ${f === 'W' ? 'bg-emerald-500' : 'bg-rose-400'}`} />)}
          </span>
        </p>
      </div>
      <div className="text-right">
        <p className="font-display font-black tabular-nums text-base">{p.rating}</p>
        <p className={`text-xs font-bold tabular-nums ${deltaClass(p.last_delta)}`}>{fmtDelta(p.last_delta)}</p>
      </div>
    </li>
  );
}
